import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

function TestimonialCard({ t, colorClass }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxLength = 160;
  const isLongText = t.text.length > maxLength;

  const displayText = isLongText && !isExpanded
    ? `${t.text.slice(0, maxLength).trim()}...`
    : t.text;

  return (
    <div className="min-w-[280px] sm:min-w-[340px] lg:min-w-0 flex-1 snap-start snap-always">
      <div className={`bg-white rounded-3xl p-6 border border-orange-100 transition-all duration-300 hover:border-orange-300 hover:-translate-y-1 flex flex-col justify-between group relative overflow-hidden ${isExpanded ? 'h-auto min-h-[250px]' : 'h-[250px]'}`}>
        <div className="absolute -bottom-4 -right-4 text-9xl text-orange-50/30 font-serif leading-none select-none pointer-events-none transition-transform duration-500 group-hover:scale-110 group-hover:text-orange-100/40">"</div>
        
        <div className="relative z-10 flex flex-col h-full justify-between">
          <div>
            {/* User Profile Header */}
            <div className="flex items-center gap-3 mb-2.5">
              <div className={`w-10 h-10 rounded-full border-2 shrink-0 flex items-center justify-center font-bold text-lg uppercase ${colorClass}`}>
                {t.name.charAt(0)}
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-base leading-tight">{t.name}</h4>
                <p className="text-xs text-gray-500">{t.role}</p>
              </div>
            </div>

            {/* 5 Stars */}
            <div className="flex gap-1 mb-2.5">
              {[...Array(t.rating)].map((_, i) => (
                <svg 
                  key={i} 
                  className="w-4 h-4 fill-current text-orange-500" 
                  viewBox="0 0 20 20" 
                  fill="currentColor"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>

            {/* Review Text */}
            <p className="text-gray-600 text-sm leading-relaxed text-left whitespace-pre-line">
              {displayText}
            </p>
          </div>

          {/* View More / View Less Toggle - Centered at bottom with reduced spacing */}
          {isLongText && (
            <div className="pt-1.5 text-center flex justify-center w-full">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-orange-500 hover:text-orange-600 focus:outline-none transition-colors group/btn"
              >
                <span>{isExpanded ? 'View Less' : 'View More'}</span>
                <svg 
                  className={`w-3.5 h-3.5 transition-transform duration-300 ${isExpanded ? 'rotate-180' : 'group-hover/btn:translate-y-0.5'}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Testimonials() {
  const testimonials = [
    {
      id: 1,
      name: "Priyanka",
      role: "Student",
      rating: 5,
      text: `Before joining the course, I was totally confused about where to start. The classes are very practical and easy to understand. Now I have a clear idea about what to do step by step.\n\nI liked the way the course is taught. It’s not just theory. We actually work on the process and understand how things work practically. Very useful for beginners.`
    },
    {
      id: 2,
      name: "Anitha",
      role: "Student",
      rating: 5,
      text: `Thank you very much for today's eCommerce live class. It was my first session, and I am truly satisfied with the way you explained everything.\n\nI already had little bit basic knowledge about product selection, but your class gave me much more clarity. You also patiently cleared all my small doubts, which really boosted my confidence 👍🏻.\n\nI sincerely look forward to your guidance and support throughout my eCommerce journey. Thank you once again for sharing your valuable knowledge 🙏🏻, Sir.\n\nI hope to learn a lot from your future sessions as well. 😊`
    },
    {
      id: 3,
      name: "Bhavani",
      role: "Student",
      rating: 5,
      text: `The best thing I liked is the product selection part. Earlier I used to randomly select products. Now I know how to properly research before investing money.`
    },
    {
      id: 4,
      name: "Lakmesh",
      role: "Student",
      rating: 5,
      text: `First of all, I would like to thank my trainers, Mani Brother and Anitha Sister, for their excellent guidance. They teach with a lot of patience and always take the time to clear every doubt, which makes learning very comfortable.\n\nTheir classes are always interesting and engaging. I never feel bored during the sessions because they explain every topic in a simple and easy-to-understand way. Their teaching style makes even complex concepts easy to learn and remember.\n\nThank you both for your dedication, support, and encouragement. I truly appreciate your efforts and enjoy attending your classes.`
    },
    {
      id: 5,
      name: "Yasmin",
      role: "Student",
      rating: 5,
      text: `I am completely new to e-com and wanted to learn everything from scratch. Your session was a great start for me. Thank you so much for conducting it. I look forward to attending more classes, especially practical sessions, to gain hands-on experience.`
    },
    {
      id: 6,
      name: "Vigneswaran",
      role: "Student",
      rating: 5,
      text: `I joined mainly to understand Amazon selling. But I learned much more than I expected, especially about product research, listing and PPC. Really practical course.`
    },
    {
      id: 7,
      name: "Yamini",
      role: "Student",
      rating: 5,
      text: `As a complete beginner, I was scared to start because I didn’t know anything about e-commerce. Now I feel much more confident to start my business.`
    },
    {
      id: 8,
      name: "Subhasini",
      role: "Student",
      rating: 5,
      text: `What I liked most is that the focus is on actually doing things, not just listening to classes. That made a big difference for me. Good course for anyone seriously planning to start.`
    },
    {
      id: 9,
      name: "Rubesh",
      role: "Student",
      rating: 5,
      text: `I was looking for someone who would explain e-commerce without using too much complicated terminology. This course was exactly that. Simple explanation, practical guidance and good support throughout.`
    }
  ];

  const [activeDot, setActiveDot] = useState(0);
  const scrollRef = useRef(null);
  const totalDots = 5;

  // Check scroll position to update dots
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, clientWidth, scrollWidth } = scrollRef.current;
    
    const maxScroll = scrollWidth - clientWidth;
    if (maxScroll <= 0) return;
    
    const percentage = scrollLeft / maxScroll;
    const dotIndex = Math.min(totalDots - 1, Math.max(0, Math.round(percentage * (totalDots - 1))));
    setActiveDot(dotIndex);
  };

  const scrollToDot = (dotIndex) => {
    if (!scrollRef.current) return;
    const { clientWidth, scrollWidth } = scrollRef.current;
    const maxScroll = scrollWidth - clientWidth;
    
    const scrollTarget = (dotIndex / (totalDots - 1)) * maxScroll;
    
    scrollRef.current.scrollTo({
      left: scrollTarget,
      behavior: 'smooth'
    });
    setActiveDot(dotIndex);
  };

  return (
    <section id="testimonials" className="py-16 bg-gradient-to-tr from-white via-amber-50/10 to-orange-50/20 relative overflow-hidden dot-pattern">
      <div className="absolute top-0 right-0 w-96 h-96 -mr-20 -mt-20 rounded-full bg-amber-100 opacity-40 blur-3xl animate-pulse"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 -ml-20 -mb-20 rounded-full bg-orange-100 opacity-40 blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
      
      <div className="absolute -top-10 left-[5%] text-[250px] leading-none text-orange-50/40 font-serif font-black select-none pointer-events-none z-0 rotate-12">"</div>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        {/* Heading */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">
            What <span className="text-orange-500">Our Students</span> Say
          </h2>
          <p className="text-gray-500 text-sm md:text-base font-medium">
            Real people, Real stories.
          </p>
        </div>

        {/* Testimonials Cards Slider/Grid */}
        <div 
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex lg:grid lg:grid-cols-3 gap-6 overflow-x-auto lg:overflow-x-visible scrollbar-none snap-x snap-mandatory pb-6 px-1 items-stretch"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {testimonials.map((t, index) => {
            const avatarColors = [
              "bg-blue-100 text-blue-600 border-blue-100",
              "bg-green-100 text-green-600 border-green-100",
              "bg-purple-100 text-purple-600 border-purple-100",
              "bg-rose-100 text-rose-600 border-rose-100",
              "bg-teal-100 text-teal-600 border-teal-100",
              "bg-amber-100 text-amber-600 border-amber-100",
              "bg-indigo-100 text-indigo-600 border-indigo-100",
              "bg-pink-100 text-pink-600 border-pink-100",
              "bg-emerald-100 text-emerald-600 border-emerald-100"
            ];
            const colorClass = avatarColors[index % avatarColors.length];
            return (
              <TestimonialCard key={t.id} t={t} colorClass={colorClass} />
            );
          })}
        </div>

        {/* Carousel Indicator Dots (Hidden on large desktop grid) */}
        <div className="flex lg:hidden justify-center items-center gap-2 mt-8">
          {[...Array(totalDots)].map((_, dotIndex) => (
            <button
              key={dotIndex}
              onClick={() => scrollToDot(dotIndex)}
              className={`h-2.5 rounded-full transition-all duration-300 ${
                activeDot === dotIndex 
                  ? 'w-8 bg-gradient-to-r from-orange-500 to-amber-500' 
                  : 'w-2.5 bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to slide ${dotIndex + 1}`}
            />
          ))}
        </div>

        <div className="mt-16 flex justify-center z-10 relative">
          <Link to="/login" className="shimmer-btn inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold py-4 px-10 rounded-full transition-transform duration-300 hover:scale-105">
            Start Your Journey Now
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}


